import type { Route, Routes } from '@angular/router';
import { permissionGuard } from '../../../../core/guards/permission.guard';
import { TasksContextStore } from './shared/state/tasks-context.store';
import { TasksReferenceStore } from './shared/state/tasks-reference.store';

/**
 * D4 (the Performance D61 rule): every tasks page is re-checked on in-area navigation too — the admin shell's
 * `permissionGuard` runs only when the shell itself is entered. Redirect-only routes are left alone: Angular rejects
 * `canActivate` beside `redirectTo`, and the redirect's target is guarded anyway.
 */
export function withTasksPermissionGuard(routes: Routes): Routes {
  return routes.map((route): Route =>
    route.loadComponent || route.component
      ? { ...route, canActivate: [...(route.canActivate ?? []), permissionGuard] }
      : route,
  );
}

/**
 * The project workspace (`/projects/:key/…`; M8 moved it out of `/tasks/projects/:key`). Mounted under the `projects`
 * route in `admin-child.routes.ts`, which provides the Tasks visit-scoped stores; every view re-checks permissions.
 */
export const PROJECT_WORKSPACE_ROUTE: Route = withTasksPermissionGuard([
  {
    path: ':key',
    // Provides the ProjectWorkspaceStore its views share; the header and tabs are its own.
    loadComponent: () =>
      import('./projects/workspace/project-workspace.component').then((m) => m.ProjectWorkspaceComponent),
    children: withTasksPermissionGuard([
      // SP5b D1: the workspace opens on the Board.
      { path: '', pathMatch: 'full', redirectTo: 'board' },
      {
        // SP5b T2: columns + drag-and-drop, the keyboard Move menu, the sprint header on Scrum.
        path: 'board',
        loadComponent: () => import('./projects/board/board-page.component').then((m) => m.BoardPageComponent),
      },
      // Scrum only (the tabs hide them on Kanban; a typed URL shows "This project has no sprints").
      {
        // SP5b T3: the sprint sections and the Backlog, drag to rank and to move, the keyboard Move menu.
        path: 'backlog',
        loadComponent: () => import('./projects/backlog/backlog-page.component').then((m) => m.BacklogPageComponent),
      },
      {
        // SP5b T4: Active / Future / Closed; create, edit, start, close with carry-over, delete, reorder.
        path: 'sprints',
        loadComponent: () => import('./projects/sprints/sprints-page.component').then((m) => m.SprintsPageComponent),
      },
      {
        path: 'list',
        loadComponent: () =>
          import('./projects/list/project-items-page.component').then((m) => m.ProjectItemsPageComponent),
      },
      {
        // SP5b T6 (D5): gated by Tasks.Reports.View / Manage (tasksAreaPermissions), not by the project's own rights,
        // AND by the view group the workspace reads with (tasksAreaAlsoRequires; final review, item 1).
        path: 'reports',
        loadComponent: () =>
          import('./projects/reports/project-reports-page.component').then((m) => m.ProjectReportsPageComponent),
      },
      {
        // Details / Members / Workflow / Components; the page itself shows 'no access' when the project allows none.
        path: 'settings',
        loadComponent: () =>
          import('./projects/settings/project-settings-page.component').then((m) => m.ProjectSettingsPageComponent),
      },
    ]),
  },
])[0]!;

export const TASKS_ROUTE: Route = {
  path: 'tasks',
  // One instance per Tasks visit, shared by every tasks page; both reset when the user leaves /tasks.
  providers: [TasksContextStore, TasksReferenceStore],
  children: withTasksPermissionGuard([
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () => import('./my-work/my-work-page.component').then((m) => m.MyWorkPageComponent),
    },
    // M8 (TaskProject → Project merge): the projects list and workspace moved to `/projects`; old links still work.
    { path: 'projects', pathMatch: 'full', redirectTo: '/projects' },
    { path: 'projects/:key', pathMatch: 'full', redirectTo: '/projects/:key' },
    { path: 'projects/:key/:view', pathMatch: 'full', redirectTo: '/projects/:key/:view' },
    {
      // D5: the full item page; the side panel over My Work and the project List opens the same detail by ?item=.
      path: 'items/:key',
      loadComponent: () => import('./items/work-item-page.component').then((m) => m.WorkItemPageComponent),
    },
    {
      // SP5c T3: Delegated to me / Delegated by me (?seg=in|out); rows open the side panel (?item=KEY) as on My Work.
      path: 'delegations',
      loadComponent: () => import('./delegations/delegations-page.component').then((m) => m.DelegationsPageComponent),
    },
    {
      // SP5b D5 / T7: the people report, and SP5c T4 the delegation report behind the same page's switch (?report=)
      // (Tasks.Reports.View / Manage; ruling W3: a reports-only caller reaches it by URL).
      path: 'reports',
      loadComponent: () => import('./reports/people-report-page.component').then((m) => m.PeopleReportPageComponent),
    },
    {
      // D1/D4: Types / Labels / Settings, the whole area gated by WnPerms.tasks.settings (tasksAreaPermissions).
      path: 'setup',
      loadComponent: () => import('./setup/setup-page.component').then((m) => m.SetupPageComponent),
      children: withTasksPermissionGuard([
        { path: '', pathMatch: 'full', redirectTo: 'types' },
        {
          path: 'types',
          loadComponent: () => import('./setup/types/setup-types.component').then((m) => m.SetupTypesComponent),
        },
        {
          path: 'labels',
          loadComponent: () => import('./setup/labels/setup-labels.component').then((m) => m.SetupLabelsComponent),
        },
        {
          path: 'settings',
          loadComponent: () => import('./setup/settings/setup-settings.component').then((m) => m.SetupSettingsComponent),
        },
      ]),
    },
  ]),
};
